import { useEffect } from "react";
import { dismissKeyboard } from "@/lib/dismissKeyboard";
import { isNative } from "@/lib/platform";

/**
 * GlobalKeyboard — app-wide keyboard ergonomics, mounted once near the root.
 *
 * Two global affordances make the soft keyboard easy to dismiss everywhere
 * (the Dex search input and every other field):
 *
 *  1. Native accessory bar (iOS): @capacitor/keyboard's setAccessoryBarVisible
 *     adds the toolbar that sits ON the keyboard itself, giving a built-in
 *     "Done" affordance to toggle the keyboard off. This is the keyboard-level
 *     dismiss the user asked for. No-op on web / where the plugin is absent.
 *
 *  2. Tap-outside-to-dismiss (all platforms): tapping anywhere that is not a
 *     text field blurs the focused input, which closes the keyboard. Acts as
 *     the universal fallback (web/Android) and a second escape hatch on iOS.
 *
 * Renders nothing.
 */
export function GlobalKeyboard() {
  useEffect(() => {
    const subs: Array<{ remove?: () => void }> = [];
    // 1. Enable the native keyboard accessory bar (the on-keyboard toolbar
    //    with a Done button). Best-effort: absent on web and harmless if the
    //    plugin version lacks the method.
    void (async () => {
      try {
        const mod: any = await import("@capacitor/keyboard" as any);
        const Keyboard = mod?.Keyboard ?? mod;
        await Keyboard?.setAccessoryBarVisible?.({ isVisible: true });
      } catch {
        /* not a Capacitor runtime / method unavailable — tap-outside covers it. */
      }
    })();

    // 1b. The keyboard OVERLAYS the page on iOS (capacitor.config.ts
    //     Keyboard.resize 'none', 2026-09-04): the webview no longer resizes,
    //     so nothing re-lays out and no dark band shows. In exchange this
    //     keeps the focused field reachable: the keyboard's height becomes
    //     bottom padding on the document (via --keyboard-h) and the field is
    //     scrolled into view above it.
    void (async () => {
      try {
        const mod: any = await import("@capacitor/keyboard" as any);
        const Keyboard = mod?.Keyboard ?? mod;
        if (!Keyboard?.addListener) return;
        const show = await Keyboard.addListener("keyboardWillShow", (info: { keyboardHeight?: number }) => {
          const h = Math.max(0, Math.round(info?.keyboardHeight ?? 0));
          (window as unknown as { __nxKeyboardMarks?: { show: () => void } }).__nxKeyboardMarks?.show();
          root.style.setProperty("--keyboard-h", `${h}px`);
          root.classList.add("keyboard-open");
          const active = document.activeElement as HTMLElement | null;
          if (active && isTextField(active)) {
            // After the padding lands, so the scroll has room to work with.
            requestAnimationFrame(() => scrollFieldAboveKeyboard(active, h));
            setTimeout(() => scrollFieldAboveKeyboard(active, h), 400);
          }
        });
        const hide = await Keyboard.addListener("keyboardWillHide", () => {
          (window as unknown as { __nxKeyboardMarks?: { hide: () => void } }).__nxKeyboardMarks?.hide();
          root.style.setProperty("--keyboard-h", "0px");
          root.classList.remove("keyboard-open");
        });
        subs.push(show, hide);
      } catch {
        /* not a Capacitor runtime */
      }
    })();

    // 1c. Belt and braces (2026-09-10 owner screenshots: on the sign-up page
    //     the email field sat under the keyboard and the page would not
    //     scroll, so trainers could not sign in). The plugin's events above
    //     are the primary signal, but a focused field must be reachable even
    //     when they never arrive. Two independent signals cover that:
    //       - visualViewport shrinks under the keyboard; its height deficit is
    //         the keyboard height, applied the same way.
    //       - focusin on a text field is certain. If nothing has reported a
    //         keyboard shortly after, a conservative height is assumed so the
    //         page gains room and the field scrolls into view above it.
    //     Native only: on the web a hardware keyboard would get a fake band.
    const root = document.documentElement;
    const native = isNative();
    let pluginReported = false;
    const applyKeyboard = (h: number) => {
      root.style.setProperty("--keyboard-h", `${Math.max(0, Math.round(h))}px`);
      root.classList.toggle("keyboard-open", h > 0);
    };
    const vv = typeof window !== "undefined" ? window.visualViewport : null;
    const onViewport = () => {
      if (!vv) return;
      const deficit = window.innerHeight - vv.height - vv.offsetTop;
      if (deficit > 80) applyKeyboard(deficit);
      else if (!pluginReported && !root.classList.contains("keyboard-assumed")) applyKeyboard(0);
    };
    vv?.addEventListener("resize", onViewport);
    vv?.addEventListener("scroll", onViewport);

    let focusTimer: ReturnType<typeof setTimeout> | null = null;
    const onFocusIn = (e: FocusEvent) => {
      if (!native) return;
      const el = e.target as HTMLElement | null;
      if (!el || !isTextField(el)) return;
      if (focusTimer) clearTimeout(focusTimer);
      focusTimer = setTimeout(() => {
        if (!root.classList.contains("keyboard-open")) {
          root.classList.add("keyboard-assumed");
          applyKeyboard(window.innerHeight * 0.45);
        }
        const assumed = parseInt(root.style.getPropertyValue("--keyboard-h"), 10) || 0;
        requestAnimationFrame(() => scrollFieldAboveKeyboard(el, assumed));
        setTimeout(() => scrollFieldAboveKeyboard(el, assumed), 400);
      }, 350);
    };
    const onFocusOut = () => {
      if (!native) return;
      setTimeout(() => {
        const active = document.activeElement as HTMLElement | null;
        if (active && isTextField(active)) return;
        if (root.classList.contains("keyboard-assumed")) {
          root.classList.remove("keyboard-assumed");
          if (!pluginReported) applyKeyboard(0);
        }
      }, 120);
    };
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    const markPluginShow = () => {
      pluginReported = true;
      root.classList.remove("keyboard-assumed");
    };
    const markPluginHide = () => {
      pluginReported = false;
      root.classList.remove("keyboard-assumed");
    };
    (window as unknown as { __nxKeyboardMarks?: { show: () => void; hide: () => void } }).__nxKeyboardMarks = {
      show: markPluginShow,
      hide: markPluginHide,
    };

    // 2. Tap-outside-to-dismiss. Only acts when a text field is focused and the
    //    pointer lands on a non-interactive, non-field element, so it never
    //    fights a tap that moves focus to another input or activates a control.
    const onPointerDown = (e: PointerEvent) => {
      const active = document.activeElement as HTMLElement | null;
      if (!active || !isTextField(active)) return;
      const target = e.target as HTMLElement | null;
      if (target && (target === active || isInteractive(target))) return;
      void dismissKeyboard();
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
      vv?.removeEventListener("resize", onViewport);
      vv?.removeEventListener("scroll", onViewport);
      if (focusTimer) clearTimeout(focusTimer);
      for (const s of subs) s.remove?.();
    };
  }, []);

  return null;
}

/**
 * Scroll so the field's bottom edge clears the keyboard (2026-09-11 owner
 * screenshot: the sign-up email field sat in the middle of the screen, under
 * the keyboard, and did not move). With the keyboard overlaying the page the
 * layout viewport keeps its full height, so scrollIntoView sees a field that
 * is already "visible" and does nothing. This measures the overlap with the
 * keyboard's band and scrolls the field's scroller by exactly that.
 */
export function scrollFieldAboveKeyboard(el: HTMLElement, keyboardHeight: number): void {
  if (!(keyboardHeight > 0)) return;
  const margin = 24;
  const limit = window.innerHeight - keyboardHeight - margin;
  const rect = el.getBoundingClientRect();
  if (rect.bottom <= limit) return;
  scrollOwnersBy(el, Math.ceil(rect.bottom - limit));
}

/**
 * Scroll whichever owner can actually move: the nearest scrolling ancestor,
 * then body, then the document. Which of those owns the page's scroll varies
 * (hooks/useScrollResetOnRoute resets all of them for the same reason), so
 * the first one with room takes the scroll and the window is the fallback.
 */
function scrollOwnersBy(el: HTMLElement, delta: number): void {
  const owners: Element[] = [];
  let node: HTMLElement | null = el.parentElement;
  while (node) {
    owners.push(node);
    node = node.parentElement;
  }
  const doc = document.scrollingElement;
  if (doc && !owners.includes(doc)) owners.push(doc);
  for (const owner of owners) {
    const isRoot = owner === document.body || owner === document.documentElement;
    if (!isRoot) {
      const style = getComputedStyle(owner);
      if (!/(auto|scroll)/.test(style.overflowY)) continue;
    }
    const room = owner.scrollHeight - owner.clientHeight - owner.scrollTop;
    if (room > 1) {
      owner.scrollBy({ top: Math.min(delta, room), behavior: "smooth" });
      return;
    }
  }
  window.scrollBy({ top: delta, behavior: "smooth" });
}

function isTextField(el: HTMLElement): boolean {
  const tag = el.tagName;
  return (
    tag === "INPUT" ||
    tag === "TEXTAREA" ||
    el.isContentEditable === true
  );
}

/** A tap on these should keep focus / run its own handler, not dismiss. */
function isInteractive(el: HTMLElement): boolean {
  return Boolean(
    el.closest(
      "input, textarea, select, button, a, label, [contenteditable], [role='button']",
    ),
  );
}
